<script setup>
import { ref, computed } from 'vue';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

// Visual unico das abas "Contato(s)" / "Empresa(s)" de Contato, Empresa e
// Negociacao: busca -> confirmacao -> lista com "Abrir" e desvincular.
// So apresentacao: quem usa busca, grava e traduz (via `labels`).
const props = defineProps({
  // [{ id, name, subtitle, avatarUrl }]
  items: { type: Array, default: () => [] },
  // candidato escolhido na busca, aguardando confirmacao
  candidate: { type: Object, default: null },
  // [{ value, label }]
  options: { type: Array, default: () => [] },
  labels: { type: Object, required: true },
  countLabel: { type: String, default: '' },
  isBusy: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'select',
  'cancel',
  'confirm',
  'open',
  'remove',
]);

const selectedId = ref(null);
const hasItems = computed(() => props.items.length > 0);

const handleSelect = id => {
  selectedId.value = null;
  emit('select', id);
};
</script>

<template>
  <div class="flex flex-col gap-6 pb-8">
    <div v-if="!candidate" class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-base text-n-slate-12">{{ labels.addTitle }}</label>
        <span class="text-sm text-n-slate-11">{{ labels.addDescription }}</span>
      </div>
      <ComboBox
        use-api-results
        :model-value="selectedId"
        :options="options"
        :disabled="isBusy"
        :empty-state="labels.emptySearch"
        :search-placeholder="labels.searchPlaceholder"
        :placeholder="labels.addTitle"
        class="[&>div>button]:bg-n-alpha-black2"
        @search="emit('search', $event)"
        @update:model-value="handleSelect"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-base text-n-slate-12">
          {{ labels.confirmTitle }}
        </label>
        <span class="text-sm text-n-slate-11">
          {{ labels.confirmDescription }}
        </span>
      </div>

      <slot name="confirm">
        <div
          class="border border-n-strong h-[60px] gap-2 flex items-center rounded-xl p-3"
        >
          <Avatar
            :name="candidate.name"
            :src="candidate.avatarUrl"
            :size="32"
            rounded-full
            hide-offline-status
          />
          <div class="flex flex-col w-full min-w-0 gap-1">
            <span
              class="text-sm leading-4 font-medium truncate text-n-slate-12"
            >
              {{ candidate.name }}
            </span>
            <span
              v-if="candidate.subtitle"
              class="text-sm leading-4 truncate text-n-slate-11"
            >
              {{ candidate.subtitle }}
            </span>
          </div>
        </div>
      </slot>

      <div class="flex items-center justify-between gap-3 mt-2">
        <Button
          variant="faded"
          color="slate"
          :label="labels.cancel"
          class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
          :disabled="isBusy"
          @click="emit('cancel')"
        />
        <Button
          :label="labels.confirm"
          class="w-full"
          :is-loading="isBusy"
          :disabled="isBusy"
          @click="emit('confirm')"
        />
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ labels.sectionTitle }}
        </h4>
        <span
          v-if="hasItems && countLabel"
          class="text-xs tabular-nums text-n-slate-11"
        >
          {{ countLabel }}
        </span>
      </div>

      <div
        v-if="isLoading && !hasItems"
        class="py-8 text-sm text-center rounded-xl border border-dashed border-n-weak text-n-slate-11"
      >
        {{ labels.loading }}
      </div>

      <div
        v-else-if="!hasItems"
        class="py-8 px-4 text-sm text-center rounded-xl border border-dashed border-n-strong text-n-slate-11"
      >
        {{ labels.empty }}
      </div>

      <div
        v-else
        class="flex flex-col divide-y divide-n-weak [&>*:hover]:!border-y-transparent [&>*:hover+*]:!border-t-transparent"
      >
        <div
          v-for="item in items"
          :key="item.id"
          role="button"
          tabindex="0"
          :title="labels.open"
          :aria-label="`${labels.open}: ${item.name}`"
          class="flex items-center gap-3 px-3 py-3 transition-all duration-300 ease-in-out cursor-pointer rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand group/item"
          @click="emit('open', item.id)"
          @keydown.enter.self="emit('open', item.id)"
          @keydown.space.prevent.self="emit('open', item.id)"
        >
          <Avatar
            :name="item.name"
            :src="item.avatarUrl"
            :size="32"
            rounded-full
            hide-offline-status
          />
          <div class="flex-1 min-w-0 space-y-0.5">
            <span
              class="block text-sm font-medium leading-5 truncate text-n-slate-12"
            >
              {{ item.name }}
            </span>
            <p
              v-if="item.subtitle"
              class="text-sm leading-5 truncate text-n-slate-11"
            >
              {{ item.subtitle }}
            </p>
          </div>
          <Button
            icon="i-lucide-unlink"
            color="slate"
            variant="ghost"
            size="xs"
            class="shrink-0 opacity-70 transition-opacity sm:opacity-0 sm:group-hover/item:opacity-100 sm:focus-visible:opacity-100"
            :disabled="isBusy"
            :title="labels.remove"
            :aria-label="labels.remove"
            @click.stop="emit('remove', item.id)"
          />
          <span
            class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-10 transition-transform duration-300 ease-in-out group-hover/item:translate-x-0.5 group-hover/item:text-n-slate-12"
            aria-hidden="true"
          />
        </div>
      </div>

      <slot name="footer" />
    </div>
  </div>
</template>
